import { useEffect } from "react";

export type DocumentMeta = {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  jsonLdId?: string;
  /** Overrides the robots meta, e.g. "noindex" for the 404 page. */
  robots?: string;
};

const PROD_ORIGIN = "https://pabs.online";

const resolveCanonical = (path?: string) => {
  if (!path) return undefined;
  try {
    const isProd =
      typeof window !== "undefined" &&
      window.location.hostname.endsWith("pabs.online");
    const origin = isProd ? PROD_ORIGIN : window.location.origin;
    return new URL(path, origin).toString();
  } catch {
    return `${PROD_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
  }
};

const upsertMetaName = (name: string, content: string) => {
  const selector = `meta[name="${name}"]`;
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
  const el = existing || document.createElement("meta");
  if (!existing) {
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  const prev = existing?.getAttribute("content");
  el.setAttribute("content", content);
  return () => {
    if (!existing) el.remove();
    else if (prev == null) el.removeAttribute("content");
    else el.setAttribute("content", prev);
  };
};

const upsertMetaProperty = (property: string, content: string) => {
  const selector = `meta[property="${property}"]`;
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
  const el = existing || document.createElement("meta");
  if (!existing) {
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  const prev = existing?.getAttribute("content");
  el.setAttribute("content", content);
  return () => {
    if (!existing) el.remove();
    else if (prev == null) el.removeAttribute("content");
    else el.setAttribute("content", prev);
  };
};

const upsertCanonical = (href: string) => {
  const existing = document.head.querySelector(
    "link[rel=canonical]"
  ) as HTMLLinkElement | null;
  const el = existing || document.createElement("link");
  if (!existing) {
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = existing?.getAttribute("href");
  el.setAttribute("href", href);
  return () => {
    if (!existing) el.remove();
    else if (prev == null) el.removeAttribute("href");
    else el.setAttribute("href", prev);
  };
};

const upsertJsonLd = (id: string, data: unknown) => {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  const el = existing || document.createElement("script");
  if (!existing) {
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  const prev = existing?.textContent;
  el.textContent = JSON.stringify(data);
  return () => {
    if (!existing) el.remove();
    else el.textContent = prev ?? null;
  };
};

/**
 * Set per-page title, description, canonical, OpenGraph, Twitter, and optional JSON-LD.
 * All mutations are reverted on unmount so client-side navigation restores prior values.
 */
export function useDocumentMeta(meta: DocumentMeta) {
  const { title, description, canonicalPath, ogImage, jsonLd, jsonLdId, robots } = meta;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const cleanup: Array<() => void> = [];

    const prevTitle = document.title;
    document.title = title;
    cleanup.push(() => {
      document.title = prevTitle;
    });

    cleanup.push(upsertMetaName("description", description));
    if (robots) cleanup.push(upsertMetaName("robots", robots));

    const canonical = resolveCanonical(canonicalPath);
    if (canonical) cleanup.push(upsertCanonical(canonical));

    cleanup.push(upsertMetaProperty("og:title", title));
    cleanup.push(upsertMetaProperty("og:description", description));
    if (canonical) cleanup.push(upsertMetaProperty("og:url", canonical));
    if (ogImage) cleanup.push(upsertMetaProperty("og:image", ogImage));

    cleanup.push(upsertMetaName("twitter:title", title));
    cleanup.push(upsertMetaName("twitter:description", description));
    if (ogImage) cleanup.push(upsertMetaName("twitter:image", ogImage));

    if (jsonLdKey) {
      const id = jsonLdId || "pabs-page-jsonld";
      cleanup.push(upsertJsonLd(id, JSON.parse(jsonLdKey)));
    }

    return () => {
      for (let i = cleanup.length - 1; i >= 0; i--) cleanup[i]();
    };
  }, [title, description, canonicalPath, ogImage, jsonLdId, jsonLdKey, robots]);
}
